import React, { useEffect, useState, useCallback } from 'react';
import './Footer.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faXTwitter, faInstagram, faTiktok, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { Link } from 'react-router-dom';
import { createFooterInstance } from '../../Models/Footer';
import { generateDynamicCSS, getComponent } from '../../ComponentsMap';
import { createSponsersInstance } from '../../Models/Sponsers';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'react-bootstrap';
import { t } from 'i18next';
import { refreshAosLite } from '../../Services/Animations/aosLite';

const Footer = ({direction} ) => {
    const { i18n } = useTranslation();
    const [footerInstance, setFooterInstance] = useState(createFooterInstance());
    const [sponsersInstance, setSponsersInstance] = useState(createSponsersInstance());
    
    const [footerData, setFooterData] = useState({
        logoText: '',
        importantLinks: [],
        contactInfo: {},
        socialLinks: [],
        copyright: ''
    });
    const [currentStyle, setCurrentStyle] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Get current language from i18n
    const currentLanguage = i18n.language;
    
    // Memoized function to load footer data
    const loadFooterData = useCallback(async (language) => {
        try {
            setIsLoading(true);
            
            await footerInstance.load(language);
            
            const loadedData = {
                logoText: footerInstance.getLogoText(),
                importantLinks: footerInstance.getImportantLinks(),
                contactInfo: footerInstance.getContactInfo(),
                socialLinks: footerInstance.getSocialLinks(),
                copyright: footerInstance.getData().copyright
            };
            
            const loadedStyles = footerInstance.getStyle();
            const loadedAnimation = footerInstance.getAnimation();
            
            setFooterData(loadedData);
            setCurrentStyle(loadedStyles);
            setAnimation(loadedAnimation);
            
            if (loadedStyles) {
                Object.entries(loadedStyles).forEach(([className, styles]) => {
                    generateDynamicCSS(className, [styles]);
                });
            }
            
            await sponsersInstance.load();
            sponsersInstance.getData();
            sponsersInstance.renderStyles();
            
            setIsLoading(false);
        } catch (err) {
            console.error('Failed to load footer data:', err);
            setError('Failed to load footer data');
            setIsLoading(false);
        }
    }, [footerInstance, sponsersInstance]);

    const [animation, setAnimation] = useState(footerInstance.getAnimation());

    // Handle language change from i18n
    const handleLanguageChange = useCallback((newLanguage) => {
        loadFooterData(newLanguage);
    }, [loadFooterData]);

    useEffect(() => {
        loadFooterData(currentLanguage);
    }, [loadFooterData, currentLanguage]);

    // Listen for language changes
    useEffect(() => {
        const handleLanguageChangeEvent = () => {
            loadFooterData(i18n.language);
        };

        i18n.on('languageChanged', handleLanguageChangeEvent);
        
        return () => {
            i18n.off('languageChanged', handleLanguageChangeEvent);
        };
    }, [i18n, loadFooterData]);

    useEffect(() => {
        refreshAosLite();
    }, [animation]);

    if (isLoading) {
        return <div className="loading-placeholder">
            <Spinner animation="border" role="status">
            </Spinner>
        </div>;
    }

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    if (!currentStyle) {
        return <div className="error-message">No styles available</div>;
    }

    // Map social link names to FontAwesome icons
    const getSocialIcon = (name) => {
        switch (name.toLowerCase()) {
            case 'facebook': return faFacebookF;
            case 'twitter': return faXTwitter;
            case 'instagram': return faInstagram;
            case 'tiktok': return faTiktok;
            case 'youtube': return faYoutube;
            default: return null;
        }
    };

    return (
        <section className="FooterSection Footer"
            dir={direction}
            {...(animation?.animationType && animation.animationType !== 'none' ? {
                'data-aos': animation.animationType,
                'data-aos-duration': Number(animation.animationDuration),
                'data-aos-delay': Number(animation.animationDelay),
            } : {})}
        >
            <div className="">
                <footer className='Footer' dir={direction}>
                    <div className="container">
                        <div className="row FooterRow">
                            {/* Logo and Description */}
                            <div className="col-lg-4 FooterCol">
                                <div className="Footerlogo">
                                    <div className="LogoContainer">
                                        <div className="LogoNavContainer FooterText">
                                        {footerInstance.getLogo() ? <img src={footerInstance.getLogo()} alt="Logo" width="200" /> : 'Logo'}
                                        </div>
                                    </div>
                                </div>
                                <div 
                                    className="FooterLogoText FooterText mt-2" 
                                    dangerouslySetInnerHTML={{ __html: footerData.logoText || '' }}
                                />
                            </div>

                            {/* Important Links */}
                            <div className="col-lg-4 col-sm-12 FooterCol mt-5">
                                {/* <span className="FooterEventsTitle FooterText">{t('ImportantLinks')}</span> */}
                                <div className="FooterEventLinks ">
                                    {footerData.importantLinks.map((link, index) => (
                                        <Link 
                                            key={index} 
                                            className="FooterEventLink FooterLink FooterText" 
                                            to={link.path}
                                        >
                                            <span>{link.name}</span>
                                        </Link>
                                    ))}
                                </div>
                            </div>

                            {/* Contact Info */}
                            {(footerData.contactInfo.phone&&footerData.contactInfo.email&&footerData.contactInfo.phone&&footerData.contactInfo.address)&&
                            
                                <div className="col-lg-4 FooterCol">
                                    <span className="FooterContactTitle FooterText">Contact Info</span>
                                    {footerData.contactInfo.address &&
                                        <div className="FooterContactItem FooterText">
                                            <b>Address: </b>
                                            <span>{footerData.contactInfo.address}</span>
                                        </div>
                                    }
                                    {footerData.contactInfo.phone &&
                                        <div className="FooterContactItem FooterText">
                                            <b>Phone: </b>
                                            <span>{footerData.contactInfo.phone}</span>
                                        </div>
                                    }
                                    {footerData.contactInfo.whatsapp &&
                                        <div className="FooterContactItem FooterText">
                                            <b>WhatsApp: </b>
                                            <span>{footerData.contactInfo.whatsapp}</span>
                                        </div>
                                    }
                                    {footerData.contactInfo.email &&
                                        <div className="FooterContactItem FooterText">
                                            <b>Email: </b>
                                            <span>{footerData.contactInfo.email}</span>
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    </div>

                    {/* Copyright & Social Media */}
                </footer>
               
            </div>
            {
                sponsersInstance.getIsVisible() && getComponent(sponsersInstance.getComponentName(), {sponsorLogos: sponsersInstance.getImages()})
            }

            <div className="container" dir={direction}>
                        <div className="row PrivacyRow">
                            <div className="col-lg-9 PrivacyCol FooterText">{footerData.copyright}</div>
                            <div className="col-lg-3 PrivacyCol">
                                <div className="FooterSocial">
                                    {footerData.socialLinks.map((social, index) => {
                                        const icon = getSocialIcon(social.name);
                                        return icon ? (
                                            <Link 
                                                key={index} 
                                                className="FooterIcon" 
                                                to={social.url}
                                            >
                                                <FontAwesomeIcon icon={icon} className='FooterText'/>
                                            </Link>
                                        ) : null;
                                    })}
                                </div>
                            </div>
                        </div>
                </div>  
        </section>
    );
};

export default Footer;